// Dashboard (/) — visão geral da operação.
// Mockup: docs/design/aeros-dark/mockups/Tela Aeros Dark - Dashboard.dc.html
//
// A saudação que o mockup desenha no topo é a do cabeçalho GLOBAL do app
// ("Boa tarde, Fulano"), que já existe acima desta tela; aqui fica o título
// com a data e o "atualizado há", para não repetir a saudação duas vezes na
// mesma dobra.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import FilterAltIcon from "@material-ui/icons/FilterList";
import DownloadIcon from "@material-ui/icons/GetApp";
import NorthIcon from "@material-ui/icons/ArrowUpward";
import SouthIcon from "@material-ui/icons/ArrowDownward";
import SupportAgentIcon from "@mui/icons-material/SupportAgentOutlined";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmptyOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAltOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOverOutlined";
import PersonAddIcon from "@material-ui/icons/PersonAddOutlined";
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnreadOutlined";
import SendIcon from "@material-ui/icons/SendOutlined";
import TimerIcon from "@material-ui/icons/TimerOutlined";
import AvTimerIcon from "@material-ui/icons/AvTimerOutlined";
import SmsIcon from "@material-ui/icons/Sms";
import moment from "moment";
import { isArray, isEmpty } from "lodash";
import * as XLSX from "xlsx";
import { useTheme, alpha } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import { ChartsFinalizationReasons } from "../../components/Dashboard/ChartsFinalizationReasons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import useMessages from "../../hooks/useMessages";
import useQueues from "../../hooks/useQueues";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useDashboardPageStyles from "./dashboardPageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { buildKpiDelta } from "./kpiDeltas";
import {
  atualizadoHa,
  agentStatus,
  agentStatusLabel,
  chartBars,
  chartTotal,
  deltaCompacto,
  formatDuracao,
  npsScore,
  npsSlices,
  npsTone,
  onlineSummary,
  queueLabel,
  ratingPercent,
  ratingTone,
} from "./dashboardRules";

// Períodos do segmentado. `custom` abre o popover em vez de buscar direto.
const RANGES = [
  { key: "today", days: 1, label: "aeros.dashboard.range.today" },
  { key: "7", days: 7, label: "aeros.dashboard.range.week" },
  { key: "30", days: 30, label: "aeros.dashboard.range.month" },
  { key: "custom", days: null, label: "aeros.dashboard.range.custom" },
];

const TOM = {
  good: { cor: AEROS.success, key: "success" },
  bad: { cor: AEROS.error, key: "error" },
  neutral: { cor: null, key: null },
};

const CORES_STATUS = {
  online: { cor: AEROS.success, key: "success" },
  busy: { cor: AEROS.warning, key: "warning" },
  offline: { cor: "#9CA3B8", key: "neutral" },
};

const iniciais = (nome = "") =>
  nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const Dashboard = () => {
  const c = useCampaignPageStyles();
  const m = useAerosModalStyles();
  const classes = useDashboardPageStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { find } = useDashboard();
  const { findAll: findAllQueues } = useQueues();

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [comparatives, setComparatives] = useState(null);
  const [serie, setSerie] = useState([]);
  const [smsSaldo, setSmsSaldo] = useState(null);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardParams, setDashboardParams] = useState(null);

  // Período e filtros aplicados (o que está na tela).
  const [range, setRange] = useState("7");
  const [dateFrom, setDateFrom] = useState(
    moment().subtract(6, "days").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [queueId, setQueueId] = useState("");
  const [userId, setUserId] = useState("");

  // Rascunho do popover: só vira filtro aplicado no "Filtrar".
  const [filtroAberto, setFiltroAberto] = useState(null);
  const [rascunho, setRascunho] = useState({});

  const [atualizadoEm, setAtualizadoEm] = useState(Date.now());
  const [agora, setAgora] = useState(Date.now());

  const gridRef = useRef(null);

  // Parâmetros da busca — memoizado para o efeito não refazer a cada render.
  const params = useMemo(() => {
    const base = {};
    const rangeAtual = RANGES.find((r) => r.key === range);
    if (rangeAtual && rangeAtual.days) {
      base.days = rangeAtual.days;
    } else {
      if (!isEmpty(dateFrom) && moment(dateFrom).isValid())
        base.date_from = moment(dateFrom).format("YYYY-MM-DD");
      if (!isEmpty(dateTo) && moment(dateTo).isValid())
        base.date_to = moment(dateTo).format("YYYY-MM-DD");
    }
    if (queueId) base.queueId = queueId;
    if (userId) base.userId = userId;
    return base;
  }, [range, dateFrom, dateTo, queueId, userId]);

  // Datas efetivas do gráfico: com "7 dias" o backend conta por days, mas a
  // série por dia precisa de um intervalo explícito.
  const intervalo = useMemo(() => {
    const rangeAtual = RANGES.find((r) => r.key === range);
    if (rangeAtual && rangeAtual.days) {
      return {
        inicio: moment()
          .subtract(rangeAtual.days - 1, "days")
          .format("YYYY-MM-DD"),
        fim: moment().format("YYYY-MM-DD"),
      };
    }
    return { inicio: dateFrom, fim: dateTo };
  }, [range, dateFrom, dateTo]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setDashboardParams(params);
    try {
      const data = await find(params);
      setCounters(data?.counters || {});
      setComparatives(data?.comparatives || null);
      setAttendants(isArray(data?.attendants) ? data.attendants : []);
    } catch (err) {
      toastError(err);
      setCounters({});
      setAttendants([]);
    }

    // Série do gráfico e saldo de SMS são complementares: falha neles não
    // pode derrubar a tela inteira.
    try {
      const { data } = await api.get("/dashboard/ticketsDay", {
        params: { initialDate: intervalo.inicio, finalDate: intervalo.fim },
      });
      setSerie(isArray(data?.data) ? data.data : []);
    } catch (err) {
      setSerie([]);
    }

    try {
      const { data } = await api.get("/sms/saldo");
      setSmsSaldo(data?.saldo ?? null);
    } catch (err) {
      setSmsSaldo(null);
    }

    setAtualizadoEm(Date.now());
    setLoading(false);
    // Dependências primitivas de propósito: `params`/`intervalo` são objetos e
    // `find` vem de um hook externo. Se qualquer um deixar de ser estável, o
    // efeito volta a disparar a cada render — foi o que aconteceu aqui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, dateFrom, dateTo, queueId, userId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    (async () => {
      try {
        const lista = await findAllQueues();
        setQueues(isArray(lista) ? lista : []);
      } catch (err) {
        setQueues([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Relógio do "atualizado há Xs". 15s é o suficiente para o texto não
  // envelhecer visivelmente, sem repintar a tela toda a cada segundo.
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // useMessages dispara um request próprio a cada chamada: fica no corpo do
  // componente, nunca dentro de map, senão a ordem dos hooks muda.
  const { count: recebidasPeriodo } = useMessages({
    fromMe: false,
    dateStart: intervalo.inicio,
    dateEnd: intervalo.fim,
  });
  const { count: enviadasPeriodo } = useMessages({
    fromMe: true,
    dateStart: intervalo.inicio,
    dateEnd: intervalo.fim,
  });
  const { count: recebidasTotal } = useMessages({ fromMe: false });
  const { count: enviadasTotal } = useMessages({ fromMe: true });

  const online = attendants.filter((a) => a.online).length;
  const deltaExtras = {
    offlineCount: attendants.length - online,
    totalReceived: recebidasTotal,
    totalSent: enviadasTotal,
  };

  const barras = useMemo(() => chartBars(serie), [serie]);
  const totalPeriodo = useMemo(() => chartTotal(serie), [serie]);

  const score = npsScore(
    counters.npsPromotersPerc,
    counters.npsDetractorsPerc
  );
  const fatias = npsSlices(counters);
  const tomScore = npsTone(score);

  const exportarXlsx = () => {
    if (!gridRef.current) return;
    const ws = XLSX.utils.table_to_sheet(gridRef.current);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendentes");
    XLSX.writeFile(wb, "relatorio-de-atendentes.xlsx");
  };

  const abrirFiltro = (e) => {
    setRascunho({
      tipo: RANGES.find((r) => r.key === range)?.days ? "quick" : "date",
      dateFrom,
      dateTo,
      queueId,
      userId,
      days: RANGES.find((r) => r.key === range)?.days || 7,
    });
    setFiltroAberto(e.currentTarget);
  };

  const aplicarFiltro = () => {
    if (rascunho.tipo === "date") {
      setRange("custom");
      setDateFrom(rascunho.dateFrom);
      setDateTo(rascunho.dateTo);
    } else {
      const alvo = RANGES.find((r) => r.days === Number(rascunho.days));
      setRange(alvo ? alvo.key : "7");
    }
    setQueueId(rascunho.queueId || "");
    setUserId(rascunho.userId || "");
    setFiltroAberto(null);
  };

  const limparFiltro = () => {
    setRascunho((prev) => ({ ...prev, queueId: "", userId: "" }));
    setQueueId("");
    setUserId("");
    setRange("7");
    setFiltroAberto(null);
  };

  const nomeFilaFiltrada = queueId
    ? queues.find((q) => String(q.id) === String(queueId))?.name
    : null;

  // Os 13 cards. "Créditos SMS" fecha a lista, como no mockup; os três que o
  // mockup não desenha (atendentes ativos e o volume de mensagens) ficaram por
  // decisão do usuário — tirá-los esconderia número que hoje é acompanhado.
  const indicadores = [
    {
      label: "Em Atendimento",
      value: counters.supportHappening || 0,
      icon: <SupportAgentIcon />,
      cor: AEROS.cyan,
      tone: "info",
      destaque: true,
    },
    {
      label: "Aguardando",
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      cor: AEROS.warning,
      tone: "warning",
    },
    {
      label: "Finalizados",
      value: counters.supportFinished || 0,
      icon: <TaskAltIcon />,
      cor: AEROS.success,
      tone: "success",
    },
    {
      label: "Grupos",
      value: counters.supportGroups || 0,
      icon: <GroupsIcon />,
      cor: "#C08BFF",
      tone: "purple",
    },
    {
      label: "Novos Contatos",
      value: counters.leads || 0,
      icon: <PersonAddIcon />,
      cor: "#F472B6",
      tone: "error",
    },
    {
      label: "Atendentes Ativos",
      value: `${online}/${attendants.length}`,
      icon: <RecordVoiceOverIcon />,
      cor: AEROS.cyan,
      tone: "info",
    },
    {
      label: "Mensagens Recebidas",
      value: recebidasPeriodo,
      icon: <MarkChatUnreadIcon />,
      cor: "#7DD3FC",
      tone: "info",
    },
    {
      label: "Mensagens Enviadas",
      value: enviadasPeriodo,
      icon: <SendIcon />,
      cor: "#A3E635",
      tone: "success",
    },
    {
      label: "T.M. de Atendimento",
      value: formatDuracao(counters.avgSupportTime),
      icon: <TimerIcon />,
      cor: "#FB923C",
      tone: "warning",
    },
    {
      label: "T.M. de Espera",
      value: formatDuracao(counters.avgWaitTime),
      icon: <AvTimerIcon />,
      cor: AEROS.error,
      tone: "error",
    },
    {
      label: "Tickets Ativos",
      value: counters.activeTickets || 0,
      icon: <NorthIcon />,
      cor: AEROS.success,
      tone: "success",
    },
    {
      label: "Tickets Passivos",
      value: counters.passiveTickets || 0,
      icon: <SouthIcon />,
      cor: "#7DD3FC",
      tone: "info",
    },
    {
      label: "Créditos SMS",
      value: smsSaldo === null ? "—" : Number(smsSaldo).toLocaleString("pt-BR"),
      icon: <SmsIcon />,
      cor: "#A3E635",
      tone: "success",
    },
  ];

  return (
    <MainContainer>
      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>{i18n.t("dashboard.title")}</div>
          <div className={classes.headerSubtitle}>
            {moment().format("dddd, D [de] MMMM")} ·{" "}
            {atualizadoHa((agora - atualizadoEm) / 1000)}
          </div>
        </div>

        <div className={c.headerActions}>
          <div className={classes.rangeGroup}>
            {RANGES.map((r) => (
              <Button
                key={r.key}
                className={`${classes.rangeButton} ${
                  range === r.key ? classes.rangeButtonActive : ""
                }`}
                onClick={(e) => (r.days ? setRange(r.key) : abrirFiltro(e))}
                aria-pressed={range === r.key}
              >
                {i18n.t(r.label)}
              </Button>
            ))}
          </div>

          <Button
            className={c.outlineButton}
            startIcon={<FilterAltIcon />}
            onClick={abrirFiltro}
          >
            {i18n.t("aeros.dashboard.queuesFilter", {
              value: nomeFilaFiltrada || i18n.t("aeros.dashboard.allQueues"),
            })}
          </Button>

          <Button
            className={c.outlineButton}
            startIcon={<DownloadIcon />}
            onClick={exportarXlsx}
          >
            {i18n.t("aeros.dashboard.export")}
          </Button>
        </div>
      </div>

      <Popover
        open={Boolean(filtroAberto)}
        anchorEl={filtroAberto}
        onClose={() => setFiltroAberto(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.filterPaper }}
      >
        <div className={classes.filterTitle}>
          {i18n.t("aeros.dashboard.filters")}
        </div>

        <div>
          <label className={m.fieldLabel}>
            {i18n.t("aeros.dashboard.filterType")}
          </label>
          <div className={classes.segmented}>
            <Button
              className={`${classes.segment} ${
                rascunho.tipo === "date" ? classes.segmentActive : ""
              }`}
              onClick={() => setRascunho((p) => ({ ...p, tipo: "date" }))}
              aria-pressed={rascunho.tipo === "date"}
            >
              {i18n.t("aeros.dashboard.byDate")}
            </Button>
            <Button
              className={`${classes.segment} ${
                rascunho.tipo === "quick" ? classes.segmentActive : ""
              }`}
              onClick={() => setRascunho((p) => ({ ...p, tipo: "quick" }))}
              aria-pressed={rascunho.tipo === "quick"}
            >
              {i18n.t("aeros.dashboard.quickFilter")}
            </Button>
          </div>
        </div>

        {rascunho.tipo === "date" ? (
          <div className={classes.filterGrid}>
            <div>
              <label className={m.fieldLabel}>
                {i18n.t("dashboard.date.initialDate")}
              </label>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                className={m.input}
                value={rascunho.dateFrom || ""}
                onChange={(e) => {
                  const valor = e.target.value;
                  setRascunho((p) => ({ ...p, dateFrom: valor }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div>
              <label className={m.fieldLabel}>
                {i18n.t("dashboard.date.finalDate")}
              </label>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                className={m.input}
                value={rascunho.dateTo || ""}
                onChange={(e) => {
                  const valor = e.target.value;
                  setRascunho((p) => ({ ...p, dateTo: valor }));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
        ) : (
          <div>
            <label className={m.fieldLabel}>
              {i18n.t("aeros.dashboard.period")}
            </label>
            <TextField
              select
              fullWidth
              variant="outlined"
              className={m.input}
              value={rascunho.days || 7}
              onChange={(e) => {
                const valor = e.target.value;
                setRascunho((p) => ({ ...p, days: valor }));
              }}
            >
              {RANGES.filter((r) => r.days).map((r) => (
                <MenuItem key={r.key} value={r.days}>
                  {i18n.t(r.label)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        )}

        <div className={classes.filterGrid}>
          <div>
            <label className={m.fieldLabel}>{i18n.t("aeros.dashboard.queue")}</label>
            <TextField
              select
              fullWidth
              variant="outlined"
              className={m.input}
              value={rascunho.queueId || ""}
              onChange={(e) => {
                const valor = e.target.value;
                setRascunho((p) => ({ ...p, queueId: valor }));
              }}
            >
              <MenuItem value="">{i18n.t("aeros.dashboard.allQueues")}</MenuItem>
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div>
            <label className={m.fieldLabel}>
              {i18n.t("aeros.dashboard.agent")}
            </label>
            <TextField
              select
              fullWidth
              variant="outlined"
              className={m.input}
              value={rascunho.userId || ""}
              onChange={(e) => {
                const valor = e.target.value;
                setRascunho((p) => ({ ...p, userId: valor }));
              }}
            >
              <MenuItem value="">{i18n.t("aeros.dashboard.allAgents")}</MenuItem>
              {attendants.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>

        <div className={classes.filterActions}>
          <Button className={m.cancelButton} onClick={limparFiltro}>
            {i18n.t("aeros.dashboard.clear")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={m.submitButton}
            onClick={aplicarFiltro}
          >
            {i18n.t("aeros.dashboard.apply")}
          </Button>
        </div>
      </Popover>

      <div className={c.pageBody}>
        <div>
          <div className={classes.sectionLabel}>
            {i18n.t("aeros.dashboard.indicators")}
          </div>
          <div className={classes.kpiGrid}>
            {indicadores.map((ind) => {
              const delta = deltaCompacto(
                buildKpiDelta(ind.label, counters, comparatives, deltaExtras)
              );
              const tomDelta = delta ? TOM[delta.tone] || TOM.neutral : null;
              return (
                <div
                  key={ind.label}
                  className={`${classes.kpiCard} ${
                    ind.destaque ? classes.kpiCardAccent : ""
                  }`}
                >
                  <div className={classes.kpiTop}>
                    <div
                      className={classes.kpiIcon}
                      style={{
                        backgroundColor: alpha(ind.cor, 0.14),
                        color: stateText(theme, ind.cor, ind.tone),
                      }}
                    >
                      {ind.icon}
                    </div>
                    {delta && (
                      <Tooltip title={delta.titulo}>
                        <span
                          className={classes.kpiDelta}
                          style={{
                            color: tomDelta?.cor
                              ? stateText(theme, tomDelta.cor, tomDelta.key)
                              : theme.palette.text.secondary,
                          }}
                        >
                          {delta.direcao === "up" && <NorthIcon />}
                          {delta.direcao === "down" && <SouthIcon />}
                          {delta.valor}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div className={classes.kpiValue}>{ind.value}</div>
                  <div className={classes.kpiLabel}>
                    {i18n.t(`aeros.dashboard.kpi.${ind.label}`, {
                      defaultValue: ind.label,
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={classes.chartRow}>
          <div className={classes.card}>
            <div className={classes.cardHead}>
              <div className={classes.cardTitle}>
                {i18n.t("aeros.dashboard.ticketsPerDay")}
              </div>
              <div className={classes.cardSub}>
                {i18n.t("aeros.dashboard.inPeriod", {
                  count: totalPeriodo.toLocaleString("pt-BR"),
                })}
              </div>
              <div className={classes.legend}>
                <span className={classes.legendItem}>
                  <span
                    className={classes.legendDot}
                    style={{ backgroundColor: "#C08BFF" }}
                  />
                  {i18n.t("aeros.dashboard.received")}
                </span>
                <span className={classes.legendItem}>
                  <span
                    className={classes.legendDot}
                    style={{ backgroundColor: AEROS.cyan }}
                  />
                  {i18n.t("aeros.dashboard.resolved")}
                </span>
              </div>
            </div>

            {barras.length === 0 ? (
              <div className={classes.chartEmpty}>
                {i18n.t("aeros.dashboard.noData")}
              </div>
            ) : (
              <div className={classes.chart}>
                {barras.map((b) => (
                  <div className={classes.chartCol} key={b.label}>
                    <div className={classes.chartBars}>
                      <Tooltip
                        title={`${i18n.t("aeros.dashboard.received")}: ${b.recebidos}`}
                      >
                        <div
                          className={`${classes.bar} ${classes.barIn}`}
                          style={{ height: `${b.pctRecebidos}%` }}
                        />
                      </Tooltip>
                      <Tooltip
                        title={`${i18n.t("aeros.dashboard.resolved")}: ${b.resolvidos}`}
                      >
                        <div
                          className={`${classes.bar} ${classes.barOut}`}
                          style={{ height: `${b.pctResolvidos}%` }}
                        />
                      </Tooltip>
                    </div>
                    <div className={classes.chartDay} title={b.label}>
                      {b.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={classes.card}>
            <div className={classes.cardTitle} style={{ marginBottom: 14 }}>
              {i18n.t("aeros.dashboard.satisfaction")}
            </div>

            <div className={classes.npsBody}>
              <div
                className={classes.donut}
                style={{
                  background: fatias.vazio
                    ? theme.palette.aeros.surface2
                    : `conic-gradient(${AEROS.success} 0% ${fatias.promoters}%, ${AEROS.warning} ${fatias.promoters}% ${
                        fatias.promoters + fatias.passive
                      }%, ${AEROS.error} ${fatias.promoters + fatias.passive}% 100%)`,
                }}
              >
                <div className={classes.donutHole}>
                  <div
                    className={classes.donutValue}
                    style={{
                      color: stateText(
                        theme,
                        tomScore === "success"
                          ? AEROS.success
                          : tomScore === "warning"
                          ? AEROS.warning
                          : AEROS.error,
                        tomScore
                      ),
                    }}
                  >
                    {score}
                  </div>
                  <div className={classes.donutLabel}>
                    {i18n.t("aeros.dashboard.score")}
                  </div>
                </div>
              </div>

              <div className={classes.npsList}>
                {[
                  {
                    label: i18n.t("aeros.dashboard.promoters"),
                    pct: counters.npsPromotersPerc || 0,
                    cor: AEROS.success,
                    tone: "success",
                  },
                  {
                    label: i18n.t("aeros.dashboard.passive"),
                    pct: counters.npsPassivePerc || 0,
                    cor: AEROS.warning,
                    tone: "warning",
                  },
                  {
                    label: i18n.t("aeros.dashboard.detractors"),
                    pct: counters.npsDetractorsPerc || 0,
                    cor: AEROS.error,
                    tone: "error",
                  },
                ].map((linha) => (
                  <div key={linha.label}>
                    <div className={classes.npsRow}>
                      <span className={classes.npsName}>{linha.label}</span>
                      <span
                        className={classes.npsPct}
                        style={{ color: stateText(theme, linha.cor, linha.tone) }}
                      >
                        {Math.round(linha.pct)}%
                      </span>
                    </div>
                    <div className={classes.track}>
                      <div
                        className={classes.trackFill}
                        style={{
                          width: `${Math.min(100, linha.pct)}%`,
                          backgroundColor: linha.cor,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.ratingBlock}>
              <div className={classes.ratingTitle}>
                {i18n.t("aeros.dashboard.ratingIndex")}
              </div>
              <div className={classes.ratingRow}>
                <div className={classes.ratingTrack}>
                  <div
                    className={classes.ratingFill}
                    style={{
                      width: `${Math.min(100, Number(counters.percRating) || 0)}%`,
                    }}
                  />
                </div>
                <div className={classes.ratingPct}>
                  {Math.round(Number(counters.percRating) || 0)}%
                </div>
              </div>

              <div className={classes.ratingGrid}>
                {[
                  { v: counters.tickets || 0, l: "aeros.dashboard.totalTickets" },
                  { v: counters.waitRating || 0, l: "aeros.dashboard.waitingRating" },
                  { v: counters.withoutRating || 0, l: "aeros.dashboard.withoutRating" },
                  { v: counters.withRating || 0, l: "aeros.dashboard.rated" },
                ].map((t) => (
                  <div className={classes.ratingTile} key={t.l}>
                    <div className={classes.ratingTileValue}>
                      {Number(t.v).toLocaleString("pt-BR")}
                    </div>
                    <div className={classes.ratingTileLabel}>{i18n.t(t.l)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Finalizações por motivo: não está no mockup, mas é a única tela que
            mostra esse recorte e acompanha o período selecionado aqui. */}
        <div className={classes.card}>
          <ChartsFinalizationReasons params={dashboardParams} />
        </div>

        <div className={`${classes.card} ${classes.cardFlush}`}>
          <div className={classes.agentsHead}>
            <div className={classes.cardTitle}>
              {i18n.t("aeros.dashboard.agents")}
            </div>
            <div className={classes.cardSub}>{onlineSummary(attendants)}</div>
            <Button
              className={classes.agentsLink}
              onClick={() => history.push("/relatorios")}
            >
              {i18n.t("aeros.dashboard.fullReport")}
            </Button>
          </div>

          <div className={classes.agentsScroll}>
          <div className={classes.agentsRowHead}>
            <div>{i18n.t("aeros.dashboard.agent")}</div>
            <div className={classes.headRight}>
              {i18n.t("aeros.dashboard.inService")}
            </div>
            <div className={classes.headRight}>
              {i18n.t("aeros.dashboard.finished")}
            </div>
            <div className={classes.headRight}>
              {i18n.t("aeros.dashboard.avgSupport")}
            </div>
            <div className={classes.headRight}>
              {i18n.t("aeros.dashboard.avgWait")}
            </div>
            <div>{i18n.t("aeros.dashboard.rating")}</div>
            <div className={classes.headCenter}>
              {i18n.t("aeros.dashboard.status")}
            </div>
          </div>

          <table
            ref={gridRef}
            style={{ display: "none" }}
            aria-hidden="true"
          >
            <thead>
              <tr>
                <th>{i18n.t("aeros.dashboard.agent")}</th>
                <th>{i18n.t("aeros.dashboard.queue")}</th>
                <th>{i18n.t("aeros.dashboard.inService")}</th>
                <th>{i18n.t("aeros.dashboard.finished")}</th>
                <th>{i18n.t("aeros.dashboard.avgSupport")}</th>
                <th>{i18n.t("aeros.dashboard.avgWait")}</th>
                <th>{i18n.t("aeros.dashboard.rating")}</th>
                <th>{i18n.t("aeros.dashboard.status")}</th>
              </tr>
            </thead>
            <tbody>
              {attendants.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{queueLabel(a.queueName, a.queueCount)}</td>
                  <td>{a.openTickets || 0}</td>
                  <td>{a.finishedTickets || 0}</td>
                  <td>{formatDuracao(a.avgSupportTime)}</td>
                  <td>{formatDuracao(a.avgWaitTime)}</td>
                  <td>{Number(a.rating || 0).toFixed(1)}</td>
                  <td>{agentStatusLabel(agentStatus(a))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading ? (
            <div className={classes.emptyRow}>{i18n.t("aeros.dashboard.loading")}</div>
          ) : attendants.length === 0 ? (
            <div className={classes.emptyRow}>
              {i18n.t("aeros.dashboard.noAgents")}
            </div>
          ) : (
            attendants.map((a, index) => {
              const status = agentStatus(a);
              const tomStatus = CORES_STATUS[status];
              const tomNota = ratingTone(a.rating, a.countRating);
              const corNota =
                tomNota === "neutral"
                  ? theme.palette.text.secondary
                  : stateText(
                      theme,
                      tomNota === "success"
                        ? AEROS.success
                        : tomNota === "warning"
                        ? AEROS.warning
                        : AEROS.error,
                      tomNota
                    );
              const corAvatar =
                AEROS.avatarCycle[index % AEROS.avatarCycle.length];
              return (
                <div className={classes.agentsRow} key={a.id}>
                  <div className={classes.agentCell}>
                    <div
                      className={classes.agentAvatar}
                      style={{ backgroundColor: corAvatar }}
                    >
                      {iniciais(a.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className={classes.agentName} title={a.name}>
                        {a.name}
                      </div>
                      <div className={classes.agentQueue}>
                        {queueLabel(a.queueName, a.queueCount)}
                      </div>
                    </div>
                  </div>
                  <div className={classes.num}>{a.openTickets || 0}</div>
                  <div className={classes.num}>{a.finishedTickets || 0}</div>
                  <div className={classes.numMuted}>
                    {formatDuracao(a.avgSupportTime)}
                  </div>
                  <div className={classes.numMuted}>
                    {formatDuracao(a.avgWaitTime)}
                  </div>
                  <div className={classes.agentRating}>
                    <div className={classes.track} style={{ flex: 1, marginTop: 0 }}>
                      <div
                        className={classes.trackFill}
                        style={{
                          width: `${ratingPercent(a.rating)}%`,
                          backgroundColor:
                            tomNota === "neutral"
                              ? theme.palette.text.secondary
                              : tomNota === "success"
                              ? AEROS.success
                              : tomNota === "warning"
                              ? AEROS.warning
                              : AEROS.error,
                        }}
                      />
                    </div>
                    <span
                      className={classes.agentRatingValue}
                      style={{ color: corNota }}
                    >
                      {Number(a.rating || 0).toFixed(1).replace(".", ",")}
                    </span>
                  </div>
                  <div className={classes.statusCell}>
                    <span
                      className={classes.statusPill}
                      style={{
                        color:
                          status === "offline"
                            ? theme.palette.text.secondary
                            : stateText(theme, tomStatus.cor, tomStatus.key),
                        backgroundColor: alpha(tomStatus.cor, 0.12),
                      }}
                    >
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: tomStatus.cor }}
                      />
                      {agentStatusLabel(status)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
          </div>
      </div>
    </MainContainer>
  );
};

export default Dashboard;
